import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { home, offices } from "@/content/site";
import { MaskedLines } from "@/components/motion/MaskedLines";
import { EASE } from "@/components/motion/Reveal";
import { ButtonLink } from "@/components/common/ButtonLink";
import { noStop } from "@/components/common/SectionHeader";

const fade = (delay) => ({ initial: { opacity: 0, y: 14 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.8, ease: EASE, delay } });

const INDEX = [
  { no: "01", label: "API and ingredient sourcing", to: "/sourcing" },
  { no: "02", label: "Licensing and market access", to: "/licensing" },
  { no: "03", label: "R&D and CRO services", to: "/rd-cro-services" },
];

// Rectangular figure with spec corners, parallax image and a linked service index beneath.
const HeroFigure = ({ progress }) => {
  const reduce = useReducedMotion();
  const imgY = useTransform(progress, [0, 1], ["0%", "14%"]);
  return (
    <motion.figure {...fade(0.3)} className="spec-corners border border-line bg-surface" data-testid="hero-image-frame">
      <div className="relative h-[300px] overflow-hidden bg-[#0E1726] sm:h-[420px] lg:h-[460px]">
        <motion.img
          src="/images/serum.webp"
          alt="A pipette releasing a single drop above a glass bottle"
          className="absolute inset-0 h-[116%] w-full object-cover"
          style={{ y: reduce ? 0 : imgY }}
          fetchPriority="high"
        />
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#0B1220]/60 via-transparent to-transparent" />
        <figcaption className="absolute left-4 top-4 bg-[#0B1220]/80 px-2.5 py-1 font-mono text-[12px] uppercase tracking-[0.12em] text-white">Fig. 01</figcaption>
        <p className="absolute bottom-4 left-4 right-4 font-mono text-[12px] uppercase tracking-[0.12em] text-white/90">One drop, many markets</p>
      </div>
      <ol className="divide-y divide-line" data-testid="hero-service-index">
        {INDEX.map((s) => (
          <li key={s.no}>
            <Link to={s.to} className="group flex items-center gap-4 px-4 py-3.5 transition-colors duration-300 hover:bg-accent-soft sm:px-5" data-testid={`hero-index-${s.no}`}>
              <span className="font-mono text-[12px] text-accent">{s.no}</span>
              <span className="flex-1 text-[15px] font-medium text-ink">{s.label}</span>
              <ArrowUpRight className="h-4 w-4 text-muted transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-accent" />
            </Link>
          </li>
        ))}
      </ol>
    </motion.figure>
  );
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const h = home.hero;

  return (
    <section ref={ref} className="relative pb-16 pt-28 sm:pt-32 lg:pb-24 lg:pt-36" data-testid="home-hero">
      <div className="container-x">
        <motion.div {...fade(0)} className="mb-12 flex flex-wrap items-center justify-between gap-3 border-y border-line py-3 font-mono text-[12px] uppercase tracking-[0.12em] text-muted lg:mb-16">
          <span className="flex items-center gap-3 text-accent">
            <span className="cross" />
            {h.eyebrow}
          </span>
          <span className="hidden sm:inline" data-testid="hero-office-cities">{offices.map((o) => o.city).join(" / ")}</span>
        </motion.div>

        <div className="grid items-start gap-12 lg:grid-cols-12 lg:gap-10">
          <div className="lg:col-span-7 lg:pt-4">
            <MaskedLines lines={h.lines.map((l, i) => (i === h.lines.length - 1 ? noStop(l) : l))} className="h-display text-ink" testId="hero-headline" />
            <motion.p className="lead mt-8 max-w-xl" {...fade(0.6)} data-testid="hero-value-prop">
              {h.value}
            </motion.p>
            <motion.div className="mt-10 flex flex-wrap items-center gap-3" {...fade(0.75)}>
              <ButtonLink to="/contact" variant="accent" testId="hero-primary-cta">
                {h.primary}
              </ButtonLink>
              <ButtonLink to="/licensing" variant="ghost" testId="hero-secondary-cta">
                {h.secondary}
              </ButtonLink>
            </motion.div>
            <motion.p className="mt-10 flex items-center gap-3 font-mono text-[12px] uppercase tracking-[0.12em] text-muted" {...fade(0.9)}>
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-accent opacity-60" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-accent" />
              </span>
              {h.caption}
            </motion.p>
          </div>
          <div className="lg:col-span-5">
            <HeroFigure progress={scrollYProgress} />
          </div>
        </div>
      </div>
    </section>
  );
};
